import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { IEnrollmentsContract } from "../../enrollments/model";
import { createEffect, createEvent, createStore, sample } from "effector";
import { ICourse, ICourseTag, ICourseWithProgress } from "@features/shared/entities/courses";
import { ICoursesContract } from "../model";
import { UserEnrollmentStatus } from "@features/shared/entities/status";
import { IStudentCourse, IStudentLesson, IStudentTopic } from "@features/shared/entities/enrollments";
import { CourseDataApi } from "@features/shared/api/course-data.api";
import { ILesson } from "@features/shared/entities/lessons";
import { IRouterContract } from "@features/shared/router";
import { IProductOnboardingContract } from "@features/onboarding/ui/productOnboarding";
import {
    continueLearningClicked,
    continueLearningModalShown,
    personalPlanClickFromHome
} from "@features/shared/events";
import { $personalPlanEnabled, $introLessons } from "@features/personal-plan/model";
import { IFilteredCoursesContract } from "./filtered-courses";
import { IAppRouteUnitsContract } from "@features/app/route-units";
import { IMetadataContract } from "@features/student/metadata/model";
import { reset } from "patronum";
import { IUserAuthContract } from "@features/student/auth/model";
import { IAmplitudeFlagsContract } from "@features/amplitude-flags/model";
import { IUiLessonContract } from "@features/lessons/ui";
import { ICurrentCourse } from "@features/shared/entities/enrollments";
import { IRoadmapsContract, resolveActiveCourse, resolveRoadmapIdForCourse } from "@features/roadmaps/model";
import { IRoadmap } from "@features/shared/entities/roadmaps";

const CONTINUE_LEARNING_LAST_CONTINUE_CLICKED_AT_KEY = "continue_learning_modal_last_continue_clicked_at";
const CONTINUE_LEARNING_INTERVAL_MS = 24 * 60 * 60 * 1000;

export const BACK_TO_LEARNING_MINUTES_PER_LESSON = 5;

export type NextMilestoneInfo = {
    courseId: number;
    title: string;
    minutes: number;
};

const parseIsoTimestamp = (raw?: string): number | null => {
    if (!raw) return null;
    const t = Date.parse(raw);
    return Number.isFinite(t) ? t : null;
};

const heroButtonClicked = createEvent<ICourseWithProgress>();
const personalPlanButtonClicked = createEvent<void>();
const lastEnrollmentSelectionResolved = createEvent<LastEnrollmentSelection | null>();
const heroEnrollmentCoursesResolved = createEvent<HeroCourseItem[]>();
const continueLastCourseButtonClicked = createEvent();
const continueLearningFlagSet = createEvent();
const backToLearningRoadmapSwitched = createEvent();
const nextMilestoneRequested = createEvent<number>();
const reEngagementFlowCompleted = createEvent();
const reEngagementContinueLearningClicked = createEvent();

const $lastEnrollmentCourse = createStore<ICourseWithProgress | null>(null).on(
    lastEnrollmentSelectionResolved,
    (_, payload) => payload?.course ?? null
);
const $lastEnrollmentCourseStatus = createStore<UserEnrollmentStatus | null>(null).on(
    lastEnrollmentSelectionResolved,
    (_, payload) => payload?.status ?? null
);
const $lastEnrollmentTrackLabel = createStore<string | null>(null);
const $learningPlanProgress = createStore<number>(0);
const $learningPlanTitle = createStore<string>("");
const $heroName = createStore<string>("");
const $hasLearningPlan = createStore<boolean>(false);
const $isContinueLearningModalOpen = createStore<boolean>(false);
const $isReEngagementWizardVisible = createStore<boolean>(false);
const $continueLearningFlag = createStore<boolean>(false);
const $continueLearningModalWasShown = createStore<boolean>(false);
const $sessionLessonActivityAt = createStore<number | null>(null);
const $roadmapsSettled = createStore<boolean>(false);
// Next incomplete topic of the course shown in the back-to-learning modal
// (title + real remaining minutes). Consumers must match courseId themselves.
const $nextMilestone = createStore<NextMilestoneInfo | null>(null);
const $heroEnrollmentCourses = createStore<HeroCourseItem[]>([]).on(
    heroEnrollmentCoursesResolved,
    (_, payload) => payload
);

const trackPersonalPlanClickFromHomeFx = createEffect(() => {
    personalPlanClickFromHome();
});

type ContinueLearningPayload = {
    courseId: number;
    sourceType: "challenge" | "course";
    levelId?: number;
    lessonId?: number;
    variant?: "control" | "test_v1";
};

const trackContinueLearningModalShownFx = createEffect(
    ({ courseId, sourceType, levelId, lessonId, variant }: ContinueLearningPayload) => {
        continueLearningModalShown(courseId, sourceType, levelId, lessonId, variant);
    }
);

const trackContinueLearningClickedFx = createEffect(
    ({ courseId, sourceType, levelId, lessonId, variant }: ContinueLearningPayload) => {
        continueLearningClicked(courseId, sourceType, levelId, lessonId, variant);
    }
);

const setContinueLearningFlagFx = createEffect((): boolean => {
    if (typeof window === "undefined") return false;
    const firstLoginFlag = localStorage.getItem("first_login");

    if (firstLoginFlag !== null) return false;
    localStorage.setItem("continue-learning", "true");
    return true;
});

const getContinueLearningFlaxFx = createEffect(() => {
    if (typeof window !== "undefined") {
        const flag = localStorage.getItem("continue-learning");

        return Boolean(flag);
    }

    return false;
});

const deleteContinueLearningFlaxFx = createEffect(() => {
    if (typeof window !== "undefined") {
        localStorage.removeItem("continue-learning");
    }
});

type LessonFlat = ILesson & {
    topicId: number;
    enrollmentStatus: UserEnrollmentStatus | null;
    startedAt: string | null;
    completedAt: string | null;
};

const resolveNextLesson = (flat: LessonFlat[]): { topicId: number; lessonId: number } | null => {
    if (flat.length === 0) return null;

    const isProgress = (l: LessonFlat) => l.enrollmentStatus === UserEnrollmentStatus.Progress;
    const isCompleted = (l: LessonFlat) => l.enrollmentStatus === UserEnrollmentStatus.Completed;
    const isCandidate = (l: LessonFlat) => l.enrollmentStatus === null || isProgress(l);

    const newestProgress = flat
        .filter((l) => isProgress(l) && !!l.startedAt)
        .sort((a, b) => new Date(b.startedAt!).getTime() - new Date(a.startedAt!).getTime())[0];
    if (newestProgress) return { topicId: newestProgress.topicId, lessonId: newestProgress.id };

    const completedSorted = flat
        .map((l, i) => ({ l, i }))
        .filter(({ l }) => isCompleted(l) && !!l.completedAt)
        .sort((a, b) => new Date(a.l.completedAt!).getTime() - new Date(b.l.completedAt!).getTime());
    const lastIdx = completedSorted.length > 0 ? completedSorted[completedSorted.length - 1].i : -1;

    if (lastIdx !== -1) {
        for (let i = lastIdx + 1; i < flat.length; i++) {
            if (isCandidate(flat[i])) return { topicId: flat[i].topicId, lessonId: flat[i].id };
        }
        for (let i = 0; i < lastIdx; i++) {
            if (isCandidate(flat[i])) return { topicId: flat[i].topicId, lessonId: flat[i].id };
        }
        return null;
    }

    return { topicId: flat[0].topicId, lessonId: flat[0].id };
};

const continueLearningNavigateFx = createEffect(
    async ({
        courseId,
        enrollmentsLessons,
        enrollmentsTopics,
        enrollmentsCourses,
        enrollCourse,
        enrollTopic,
        enrollLesson
    }: {
        courseId: number;
        enrollmentsLessons: IStudentLesson[];
        enrollmentsTopics: IStudentTopic[];
        enrollmentsCourses: IStudentCourse[];
        enrollCourse: (courseId: number) => void;
        enrollTopic: (payload: { courseId: number; topicId: number }) => void;
        enrollLesson: (payload: { topicId: number; lessonId: number }) => void;
    }): Promise<string> => {
        const courseTopicIds = new Set(
            enrollmentsTopics.filter((t) => t.course_id === courseId).map((t) => t.topic_id)
        );
        const courseEnrollments = enrollmentsLessons.filter((l) => courseTopicIds.has(l.topic_id));

        const newestProgress = courseEnrollments
            .filter((l) => l.status === UserEnrollmentStatus.Progress)
            .sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime())[0];

        if (newestProgress) {
            return `/course/${courseId}/topic/${newestProgress.topic_id}/lesson/${newestProgress.lesson_id}`;
        }

        const topics = await CourseDataApi.getTopicsByCourseId(courseId);
        const sortedTopics = topics.sort((a, b) => a.number - b.number);
        const allLessons = await Promise.all(
            sortedTopics.map((t) =>
                CourseDataApi.getLessonsByTopicId(t.id).then((ls) => ({
                    topicId: t.id,
                    lessons: ls.sort((a, b) => a.number - b.number)
                }))
            )
        );

        const enrollmentMap = new Map(enrollmentsLessons.map((e) => [e.lesson_id, e]));
        const flat: LessonFlat[] = allLessons.flatMap(({ topicId, lessons }) =>
            lessons.map((l) => {
                const e = enrollmentMap.get(l.id);
                return {
                    ...l,
                    topicId,
                    enrollmentStatus: e?.status ?? null,
                    startedAt: e?.started_at ?? null,
                    completedAt: e?.completed_at ?? null
                };
            })
        );

        const target = resolveNextLesson(flat);
        if (!target) return `/course/${courseId}`;

        if (!enrollmentsCourses.some((e) => e.course_id === courseId)) {
            enrollCourse(courseId);
        }
        if (!enrollmentsTopics.some((e) => e.topic_id === target.topicId)) {
            enrollTopic({ courseId, topicId: target.topicId });
        }
        if (!enrollmentsLessons.some((e) => e.lesson_id === target.lessonId)) {
            enrollLesson({ topicId: target.topicId, lessonId: target.lessonId });
        }

        return `/course/${courseId}/topic/${target.topicId}/lesson/${target.lessonId}`;
    }
);

type DirectChallengeFirstLessonData = {
    courseId: number;
    topicId: number;
    lessonId: number;
    lessonTitle: string;
};

const resolveDirectChallengeFirstLessonFx = createEffect(
    async (courseId: number): Promise<DirectChallengeFirstLessonData | null> => {
        try {
            const topics = await CourseDataApi.getTopicsByCourseId(courseId);
            if (!topics || topics.length === 0) return null;

            const sortedTopics = topics.slice().sort((a, b) => a.number - b.number);
            const targetTopic = sortedTopics[0];
            const lessons = await CourseDataApi.getLessonsByTopicId(targetTopic.id);
            if (!lessons || lessons.length === 0) return null;

            const sortedLessons = lessons.slice().sort((a, b) => a.number - b.number);
            const targetLesson = sortedLessons[0];
            return {
                courseId,
                topicId: targetTopic.id,
                lessonId: targetLesson.id,
                lessonTitle: targetLesson.title
            };
        } catch {
            return null;
        }
    }
);

const resolveNextMilestoneFx = createEffect(
    async ({
        courseId,
        enrollmentsTopics,
        enrollmentsLessons
    }: {
        courseId: number;
        enrollmentsTopics: IStudentTopic[];
        enrollmentsLessons: IStudentLesson[];
    }): Promise<NextMilestoneInfo | null> => {
        const topics = await CourseDataApi.getTopicsByCourseId(courseId);
        if (!topics || topics.length === 0) return null;

        const topicEnrollments = new Map(
            enrollmentsTopics.filter((e) => e.course_id === courseId).map((e) => [e.topic_id, e])
        );
        const nextTopic = topics
            .slice()
            .sort((a, b) => a.number - b.number)
            .find((topic) => {
                const enrollment = topicEnrollments.get(topic.id);
                if (!enrollment) return true;
                return enrollment.status !== UserEnrollmentStatus.Completed && (enrollment.progress ?? 0) < 100;
            });
        if (!nextTopic) return null;

        const lessons = await CourseDataApi.getLessonsByTopicId(nextTopic.id);
        const completedLessonIds = new Set(
            enrollmentsLessons.filter((l) => l.status === UserEnrollmentStatus.Completed).map((l) => l.lesson_id)
        );
        const remainingLessons = Math.max((lessons ?? []).filter((l) => !completedLessonIds.has(l.id)).length, 1);

        return {
            courseId,
            title: nextTopic.title,
            minutes: remainingLessons * BACK_TO_LEARNING_MINUTES_PER_LESSON
        };
    }
);

const checkLessonCallbackFx = createEffect((): boolean => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    const callbackUrl = params.get("callbackUrl") ?? "";
    return /\/course\/\d+\/topic\/\d+\/lesson\/\d+/.test(callbackUrl);
});

sample({
    clock: personalPlanButtonClicked,
    target: trackPersonalPlanClickFromHomeFx
});

sample({
    clock: continueLearningFlagSet,
    target: setContinueLearningFlagFx
});

const contract = new ModelContract(
    {
        $lastEnrollmentCourse,
        $heroEnrollmentCourses,
        $learningPlanProgress,
        $heroName,
        $lastEnrollmentCourseStatus,
        $learningPlanTitle,
        $lastEnrollmentTrackLabel,
        $hasLearningPlan,
        $isContinueLearningModalOpen,
        $isReEngagementWizardVisible,
        $nextMilestone,
        $nextMilestonePending: resolveNextMilestoneFx.pending
    },
    {
        heroButtonClicked,
        personalPlanButtonClicked,
        continueLastCourseButtonClicked,
        continueLearningFlagSet,
        backToLearningRoadmapSwitched,
        nextMilestoneRequested,
        reEngagementFlowCompleted,
        reEngagementContinueLearningClicked
    },
    {},
    {}
);

export type ILastEnrollmentContract = typeof contract;

export class LastEnrollmentModule extends ModuleDescription<ILastEnrollmentContract> {
    constructor(
        public enrollmentsModule: IEnrollmentsContract,
        public coursesModule: ICoursesContract,
        public filteredCoursesModule: IFilteredCoursesContract,
        public routerModule: IRouterContract,
        public productOnboardingModule: IProductOnboardingContract,
        public routesModule: IAppRouteUnitsContract,
        public metadataModule: IMetadataContract,
        public userAuthModule: IUserAuthContract,
        public amplitudeFlagsModule: IAmplitudeFlagsContract,
        public lessonUiModule: IUiLessonContract,
        public roadmapsModule: IRoadmapsContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        const $onboardingPrimaryCourseId = this.productOnboardingModule.stores.$onboardingCourse.map(
            (course) => course?.id ?? null
        );

        sample({
            clock: this.routesModule.stores.home.opened,
            target: getContinueLearningFlaxFx
        });

        sample({
            clock: getContinueLearningFlaxFx.doneData,
            target: $continueLearningFlag
        });

        sample({
            clock: setContinueLearningFlagFx.doneData,
            target: $continueLearningFlag
        });

        sample({
            clock: this.userAuthModule.effects.signInRequestedFx.done,
            target: checkLessonCallbackFx
        });

        sample({
            clock: checkLessonCallbackFx.doneData,
            filter: (hasLessonCallback) => !hasLessonCallback,
            target: continueLearningFlagSet
        });

        // Track lesson visits within the current session. Covers every way into a
        // lesson (modal CTA, roadmap, deep link) via the lesson route unit.
        sample({
            clock: this.routesModule.stores.lesson.opened,
            fn: () => Date.now(),
            target: $sessionLessonActivityAt
        });

        // Roadmaps decide which course the modal offers, so opening before the request
        // settles would show a catalog fallback and then visibly swap it for the roadmap
        // course. A failed request settles too — then the fallback is the honest answer.
        sample({
            clock: this.roadmapsModule.effects.getRoadmapsFx.finally,
            fn: () => true,
            target: $roadmapsSettled
        });

        // Single gate for the back-to-learning modal (both variants of the
        // back_to_learning_variant flag read the same open store; the variant
        // only decides WHICH component renders). Show triggers:
        //  (a) 24h cooldown since last lesson activity,
        //  (b) login (checkLessonCallbackFx -> continueLearningFlagSet),
        //  (c) coming from the landing while authorized (landing header fires continueLearningFlagSet).
        sample({
            source: {
                homeActive: this.routesModule.stores.home.$isActive,
                continueLearningFlag: $continueLearningFlag,
                metadata: this.metadataModule.stores.$metadata,
                enrollmentsLessons: this.enrollmentsModule.stores.$enrollmentsLessons,
                wasShown: $continueLearningModalWasShown,
                sessionLessonActivityAt: $sessionLessonActivityAt,
                introLessons: $introLessons,
                roadmapsSettled: $roadmapsSettled
            },
            filter: ({
                homeActive,
                continueLearningFlag,
                metadata,
                enrollmentsLessons,
                wasShown,
                sessionLessonActivityAt,
                introLessons,
                roadmapsSettled
            }) => {
                if (!homeActive) return false;

                if (!roadmapsSettled) return false;

                // Show at most once per session (resets on a new login/landing trigger)
                if (wasShown) return false;

                if (enrollmentsLessons.length === 0) {
                    return false;
                }

                const introLessonIds = new Set(introLessons.map((l) => l.lessonId));
                if (enrollmentsLessons.every((l) => introLessonIds.has(l.lesson_id))) {
                    return false;
                }

                // Explicit triggers (login / landing Login click) bypass the cooldown —
                // but not if the user has already been in a lesson this session.
                if (continueLearningFlag) {
                    return sessionLessonActivityAt === null;
                }

                const now = Date.now();

                const serverLessonActivityAt = enrollmentsLessons.reduce<number | null>((acc, l) => {
                    const tStarted = parseIsoTimestamp(l.started_at);
                    const tCompleted =
                        l.status === UserEnrollmentStatus.Completed ? parseIsoTimestamp(l.completed_at) : null;

                    const lessonMax =
                        tStarted === null
                            ? tCompleted
                            : tCompleted === null
                                ? tStarted
                                : Math.max(tStarted, tCompleted);

                    if (lessonMax === null) return acc;
                    return acc === null ? lessonMax : Math.max(acc, lessonMax);
                }, null);

                // The server data can lag behind (enrollments are not refetched instantly
                // after a lesson visit), so take the freshest of server / session activity.
                const lastLessonActivityAt =
                    serverLessonActivityAt === null
                        ? sessionLessonActivityAt
                        : sessionLessonActivityAt === null
                            ? serverLessonActivityAt
                            : Math.max(serverLessonActivityAt, sessionLessonActivityAt);

                // Main rule: show modal only if last lesson activity was >= 24h ago
                const isLessonCooldownPassed =
                    lastLessonActivityAt !== null &&
                    Math.max(0, now - lastLessonActivityAt) >= CONTINUE_LEARNING_INTERVAL_MS;

                if (!isLessonCooldownPassed) return false;

                const lastClickedAt = parseIsoTimestamp(metadata?.[CONTINUE_LEARNING_LAST_CONTINUE_CLICKED_AT_KEY]);
                // Secondary rule: also don't spam if user recently clicked "continue"
                const isClickCooldownPassed =
                    lastClickedAt === null || Math.max(0, now - lastClickedAt) >= CONTINUE_LEARNING_INTERVAL_MS;

                return isClickCooldownPassed;
            },
            fn: () => true,
            target: $isContinueLearningModalOpen
        });

        sample({
            clock: $isContinueLearningModalOpen,
            filter: (isOpen) => isOpen,
            fn: () => true,
            target: $continueLearningModalWasShown
        });

        sample({
            clock: $isContinueLearningModalOpen,
            source: {
                course: $lastEnrollmentCourse,
                enrollmentsTopics: this.enrollmentsModule.stores.$enrollmentsTopics,
                enrollmentsLessons: this.enrollmentsModule.stores.$enrollmentsLessons
            },
            filter: ({ course }, isOpen) => isOpen && course !== null,
            fn: ({ course, enrollmentsTopics, enrollmentsLessons }) => ({
                courseId: course!.id,
                enrollmentsTopics,
                enrollmentsLessons
            }),
            target: resolveNextMilestoneFx
        });

        sample({
            clock: nextMilestoneRequested,
            source: {
                enrollmentsTopics: this.enrollmentsModule.stores.$enrollmentsTopics,
                enrollmentsLessons: this.enrollmentsModule.stores.$enrollmentsLessons
            },
            fn: ({ enrollmentsTopics, enrollmentsLessons }, courseId) => ({
                courseId,
                enrollmentsTopics,
                enrollmentsLessons
            }),
            target: resolveNextMilestoneFx
        });

        sample({
            clock: resolveNextMilestoneFx.doneData,
            target: $nextMilestone
        });

        // Clear stale data as soon as a new resolution starts (fallback copy is shown meanwhile)
        reset({
            clock: [resolveNextMilestoneFx, this.userAuthModule.events.logoutStudent],
            target: $nextMilestone
        });



        sample({
            clock: [continueLastCourseButtonClicked, backToLearningRoadmapSwitched],
            target: deleteContinueLearningFlaxFx
        });

        sample({
            clock: [continueLastCourseButtonClicked, backToLearningRoadmapSwitched],
            fn: () => {
                return { key: CONTINUE_LEARNING_LAST_CONTINUE_CLICKED_AT_KEY, value: new Date().toISOString() };
            },
            target: this.metadataModule.events.setMetadataRequested
        });

        sample({
            clock: reEngagementFlowCompleted,
            target: [deleteContinueLearningFlaxFx, reEngagementContinueLearningClicked]
        });

        sample({
            clock: reEngagementFlowCompleted,
            fn: () => {
                return { key: CONTINUE_LEARNING_LAST_CONTINUE_CLICKED_AT_KEY, value: new Date().toISOString() };
            },
            target: this.metadataModule.events.setMetadataRequested
        });

        sample({
            source: {
                personalPlanEnabled: $personalPlanEnabled,
                learningPlan: this.productOnboardingModule.stores.$learningPlan
            },
            fn: ({ personalPlanEnabled, learningPlan }) => {
                return personalPlanEnabled && !!learningPlan?.plan;
            },
            target: $hasLearningPlan
        });

        sample({
            clock: personalPlanButtonClicked,
            fn: () => "/learning-plan",
            target: this.routerModule.events.routerPush
        });

        // The course to continue comes from the roadmap the student is in. Only when that
        // roadmap has nothing left to offer (or roadmaps are unavailable) do we fall back
        // to scanning the catalog by track tags — that path can surface courses that
        // belong to no roadmap at all.
        sample({
            source: {
                enrollmentCourses: this.enrollmentsModule.stores.$enrollmentsCourses,
                courses: this.coursesModule.stores.$allCourses,
                trackTags: this.filteredCoursesModule.stores.$coursesTags,
                selectedRoadmap: this.roadmapsModule.stores.$selectedRoadmap
            },
            fn: (source) => resolveRoadmapCourse(source) ?? resolveLastEnrollmentCourse(source),
            target: lastEnrollmentSelectionResolved
        });

        sample({
            source: {
                enrollmentCourses: this.enrollmentsModule.stores.$enrollmentsCourses,
                courses: this.coursesModule.stores.$allCourses,
                trackTags: this.filteredCoursesModule.stores.$coursesTags,
                primaryCourseId: $onboardingPrimaryCourseId
            },
            fn: resolveHeroEnrollmentCourses,
            target: heroEnrollmentCoursesResolved
        });

        sample({
            clock: heroButtonClicked,
            filter: (course) => course != null,
            fn: (course) => {
                if (course.tags?.includes("challenge")) {
                    return `/course/${course.id}`;
                }
                return `/${course.type}/${course.id}`;
            },
            target: this.routerModule.events.routerPush
        });

        sample({
            clock: this.productOnboardingModule.events.productV3DirectChallengeEntryStarted,
            source: this.productOnboardingModule.stores.$onboardingCourse,
            filter: (course): course is ICourse => course != null,
            fn: (course) => (course as ICourse).id,
            target: resolveDirectChallengeFirstLessonFx
        });

        sample({
            clock: resolveDirectChallengeFirstLessonFx.doneData,
            source: this.productOnboardingModule.stores.$onboardingCourse,
            filter: (_, result) => result !== null,
            fn: (course, result) => ({
                courseId: result!.courseId,
                topicId: result!.topicId,
                lessonId: result!.lessonId,
                courseData: course ? ({ ...course, progress: 0 } as ICurrentCourse) : undefined,
                analytics: {
                    courseType: (course?.type === "challenge" ? "challenge" : "course") as "challenge" | "course",
                    lessonTitle: result!.lessonTitle,
                    dayNumber: 1
                }
            }),
            target: this.lessonUiModule.events.startLessonFlowRequested
        });

        sample({
            clock: resolveDirectChallengeFirstLessonFx.doneData,
            source: this.productOnboardingModule.stores.$onboardingCourse,
            filter: (_, result) => result === null,
            fn: (course) => (course ? `/course/${course.id}` : "/home"),
            target: this.routerModule.events.routerPush
        });

        sample({
            clock: [resolveDirectChallengeFirstLessonFx.done, resolveDirectChallengeFirstLessonFx.fail],
            target: this.productOnboardingModule.events.directChallengeNavigationCompleted
        });

        sample({
            clock: continueLastCourseButtonClicked,
            source: {
                lastEnrollmentCourse: $lastEnrollmentCourse,
                enrollmentsLessons: this.enrollmentsModule.stores.$enrollmentsLessons,
                enrollmentsTopics: this.enrollmentsModule.stores.$enrollmentsTopics,
                enrollmentsCourses: this.enrollmentsModule.stores.$enrollmentsCourses
            },
            filter: ({ lastEnrollmentCourse }) => lastEnrollmentCourse !== null,
            fn: (s) => ({
                courseId: s.lastEnrollmentCourse!.id,
                enrollmentsLessons: s.enrollmentsLessons,
                enrollmentsTopics: s.enrollmentsTopics,
                enrollmentsCourses: s.enrollmentsCourses,
                enrollCourse: this.enrollmentsModule.events.enrollCourse,
                enrollTopic: this.enrollmentsModule.events.enrollTopic,
                enrollLesson: this.enrollmentsModule.events.enrollLesson
            }),
            target: continueLearningNavigateFx
        });

        sample({
            clock: reEngagementContinueLearningClicked,
            source: {
                lastEnrollmentCourse: $lastEnrollmentCourse,
                enrollmentsLessons: this.enrollmentsModule.stores.$enrollmentsLessons,
                enrollmentsTopics: this.enrollmentsModule.stores.$enrollmentsTopics,
                enrollmentsCourses: this.enrollmentsModule.stores.$enrollmentsCourses
            },
            filter: ({ lastEnrollmentCourse }) => lastEnrollmentCourse !== null,
            fn: (s) => ({
                courseId: s.lastEnrollmentCourse!.id,
                enrollmentsLessons: s.enrollmentsLessons,
                enrollmentsTopics: s.enrollmentsTopics,
                enrollmentsCourses: s.enrollmentsCourses,
                enrollCourse: this.enrollmentsModule.events.enrollCourse,
                enrollTopic: this.enrollmentsModule.events.enrollTopic,
                enrollLesson: this.enrollmentsModule.events.enrollLesson
            }),
            target: continueLearningNavigateFx
        });

        sample({
            clock: continueLearningNavigateFx.doneData,
            target: this.routerModule.events.routerPush
        });

        // The modal shows the last course with progress no matter which roadmap is
        // selected, so continuing it must move the app onto the roadmap that owns
        // that course — otherwise home keeps showing the previously selected one.
        sample({
            clock: [continueLastCourseButtonClicked, reEngagementContinueLearningClicked],
            source: {
                course: $lastEnrollmentCourse,
                roadmaps: this.roadmapsModule.stores.$roadmaps,
                selectedRoadmapId: this.roadmapsModule.stores.$selectedRoadmapId
            },
            filter: ({ course, roadmaps, selectedRoadmapId }) => {
                if (!course) return false;
                const roadmapId = resolveRoadmapIdForCourse(roadmaps, course.id, selectedRoadmapId);
                return roadmapId !== null && roadmapId !== selectedRoadmapId;
            },
            fn: ({ course, roadmaps, selectedRoadmapId }) =>
                resolveRoadmapIdForCourse(roadmaps, course!.id, selectedRoadmapId)!,
            target: this.roadmapsModule.events.selectedRoadmapChanged
        });

        sample({
            source: {
                lastEnrollmentCourse: $lastEnrollmentCourse,
                courseTags: this.filteredCoursesModule.stores.$coursesTags
            },
            filter: (s) => s.lastEnrollmentCourse !== null,
            fn: ({ lastEnrollmentCourse, courseTags }) => {
                const normalizedTags = Array.from(
                    new Set([
                        ...(lastEnrollmentCourse!.tags ?? []),
                        lastEnrollmentCourse!.type === "challenge" ? "challenge" : null
                    ])
                ).filter(Boolean) as string[];

                if (!normalizedTags.length) return null;

                const matchingTag = normalizedTags
                    .map((tag) => courseTags.find((courseTag) => courseTag.value === tag))
                    .find((tag) => tag && (tag.value.startsWith("track_") || tag.value === "challenge"));

                if (matchingTag) return matchingTag.label;

                if (lastEnrollmentCourse!.type === "challenge" || lastEnrollmentCourse!.tags?.includes("challenge")) {
                    return "Challenges";
                }

                return null;
            },
            target: $lastEnrollmentTrackLabel
        });

        sample({
            source: {
                enrollmentTopics: this.enrollmentsModule.stores.$enrollmentsTopics,
                plan: this.productOnboardingModule.stores.$learningPlan
            },
            fn: ({ enrollmentTopics, plan }) => {
                const planTopicIds = plan?.plan?.planSteps?.map((step) => step.topicId) || [];
                const relevantTopics = enrollmentTopics.filter((et) => planTopicIds.includes(et.topic_id)) || [];
                const progressSum = relevantTopics.reduce((sum, et) => sum + (et.progress || 0), 0);
                const progress = planTopicIds.length > 0 ? Math.round(progressSum / planTopicIds.length) : 0;

                return progress;
            },
            target: $learningPlanProgress
        });

        sample({
            source: {
                personalPlanEnabled: $personalPlanEnabled,
                personalProfile: this.productOnboardingModule.stores.$personalProfile
            },
            fn: ({ personalPlanEnabled, personalProfile }) => {
                if (!personalPlanEnabled) return "";

                const name = personalProfile?.profile?.name?.value;
                if (!name || !personalProfile?.profile?.name?.confident || name === "Unknown") return "";

                return name;
            },
            target: $heroName
        });

        sample({
            source: {
                personalPlanEnabled: $personalPlanEnabled,
                learningPlan: this.productOnboardingModule.stores.$learningPlan,
                heroName: $heroName
            },
            fn: ({ personalPlanEnabled, learningPlan, heroName }) => {
                if (!personalPlanEnabled || !learningPlan?.plan?.planTitle) {
                    return "Your AI learning plan";
                }

                const planTitle = learningPlan.plan.planTitle;
                if (heroName && !planTitle.startsWith(heroName)) {
                    return `${heroName}'s ${planTitle}`;
                }

                return planTitle;
            },
            target: $learningPlanTitle
        });

        sample({
            clock: continueLastCourseButtonClicked,
            source: {
                lastEnrollmentCourse: $lastEnrollmentCourse,
                enrollmentsLessons: this.enrollmentsModule.stores.$enrollmentsLessons,
                enrollmentsTopics: this.enrollmentsModule.stores.$enrollmentsTopics,
                variant: this.amplitudeFlagsModule.stores.$backToLearningVariant
            },
            filter: (s) => s.lastEnrollmentCourse !== null,
            fn: (s) => {
                const courseId = s.lastEnrollmentCourse!.id;
                const topicIds = new Set(
                    s.enrollmentsTopics.filter((t) => t.course_id === courseId).map((t) => t.topic_id)
                );
                const latest = s.enrollmentsLessons
                    .filter((l) => topicIds.has(l.topic_id) && l.status === UserEnrollmentStatus.Progress)
                    .sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime())[0];
                return {
                    courseId,
                    sourceType: (s.lastEnrollmentCourse!.type === "challenge" ? "challenge" : "course") as
                        | "challenge"
                        | "course",
                    levelId: latest?.topic_id,
                    lessonId: latest?.lesson_id,
                    variant: s.variant
                };
            },
            target: [trackContinueLearningClickedFx, deleteContinueLearningFlaxFx]
        });

        sample({
            clock: $isContinueLearningModalOpen,
            source: {
                lastEnrollmentCourse: $lastEnrollmentCourse,
                enrollmentsLessons: this.enrollmentsModule.stores.$enrollmentsLessons,
                enrollmentsTopics: this.enrollmentsModule.stores.$enrollmentsTopics,
                variant: this.amplitudeFlagsModule.stores.$backToLearningVariant
            },
            filter: (s, isOpen) => isOpen && s.lastEnrollmentCourse !== null,
            fn: (s) => {
                const courseId = s.lastEnrollmentCourse!.id;
                const topicIds = new Set(
                    s.enrollmentsTopics.filter((t) => t.course_id === courseId).map((t) => t.topic_id)
                );
                const latest = s.enrollmentsLessons
                    .filter((l) => topicIds.has(l.topic_id) && l.status === UserEnrollmentStatus.Progress)
                    .sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime())[0];
                return {
                    courseId,
                    sourceType: (s.lastEnrollmentCourse!.type === "challenge" ? "challenge" : "course") as
                        | "challenge"
                        | "course",
                    levelId: latest?.topic_id,
                    lessonId: latest?.lesson_id,
                    variant: s.variant
                };
            },
            target: trackContinueLearningModalShownFx
        });

        sample({
            clock: [this.userAuthModule.events.logoutStudent, this.routesModule.stores.home.closed],
            target: deleteContinueLearningFlaxFx
        });

        reset({
            clock: [continueLastCourseButtonClicked, backToLearningRoadmapSwitched, this.userAuthModule.events.logoutStudent],
            target: $isContinueLearningModalOpen
        });

        reset({
            clock: this.userAuthModule.events.logoutStudent,
            target: [$sessionLessonActivityAt, $roadmapsSettled]
        });

        reset({
            clock: [reEngagementFlowCompleted, reEngagementContinueLearningClicked],
            target: $isReEngagementWizardVisible
        });

        reset({
            clock: deleteContinueLearningFlaxFx.done,
            target: $continueLearningFlag
        });

        reset({
            clock: [setContinueLearningFlagFx.done, this.userAuthModule.events.logoutStudent],
            target: $continueLearningModalWasShown
        });
    }
}

type LastEnrollmentSource = {
    enrollmentCourses: IStudentCourse[];
    courses: ICourse[];
    trackTags: ICourseTag[];
    primaryCourseId?: number | null;
};

export type HeroCourseItem = {
    course: ICourseWithProgress;
    status: UserEnrollmentStatus | null;
    trackLabel: string | null;
};

type LastEnrollmentSelection = {
    course: ICourseWithProgress;
    status: UserEnrollmentStatus | null;
};

type RoadmapCourseSource = {
    selectedRoadmap: IRoadmap | null;
    enrollmentCourses: IStudentCourse[];
    courses: ICourse[];
};

const resolveRoadmapCourse = ({
    selectedRoadmap,
    enrollmentCourses,
    courses
}: RoadmapCourseSource): LastEnrollmentSelection | null => {
    // Newest in-progress course of the roadmap, otherwise its first unfinished one
    const roadmapCourse = resolveActiveCourse(selectedRoadmap);
    if (!roadmapCourse) return null;

    const enrollment = enrollmentCourses.find((e) => e.course_id === roadmapCourse.id) ?? null;
    const status = enrollment?.status ?? roadmapCourse.enrollmentStatus ?? null;
    const progress = enrollment?.progress ?? roadmapCourse.enrollmentProgress ?? null;

    // resolveActiveCourse returns the first course even when the whole roadmap is done —
    // treat that as "nothing left here" so the catalog fallback can answer instead.
    if (status === UserEnrollmentStatus.Completed || (progress ?? 0) >= 100) return null;

    // Course facts (lesson/topic counts, tags) come from the catalog; the roadmap only
    // decides which course it is.
    const course = courses.find((c) => c.id === roadmapCourse.id) ?? roadmapCourse;

    return {
        course: { ...course, progress, enrollmentUpdatedAt: enrollment?.updated_at ?? null },
        status
    };
};

const resolveLastEnrollmentCourse = ({
    enrollmentCourses,
    courses
}: LastEnrollmentSource): LastEnrollmentSelection | null => {
    if (!courses.length) return null;

    const courseMap = new Map(courses.map((course) => [course.id, course]));
    const courseEnrollments = enrollmentCourses.filter((enrollment) => courseMap.has(enrollment.course_id));
    const enrollmentsByCourseId = new Map(courseEnrollments.map((enrollment) => [enrollment.course_id, enrollment]));
    const sortedEnrollments = [...courseEnrollments].sort((a, b) => {
        const aTime = new Date(a.updated_at).getTime();
        const bTime = new Date(b.updated_at).getTime();
        const safeATime = Number.isNaN(aTime) ? 0 : aTime;
        const safeBTime = Number.isNaN(bTime) ? 0 : bTime;
        return safeBTime - safeATime;
    });
    const coursesByOrder = [...courses].sort((a, b) => a.orderNumber - b.orderNumber);

    const toSelection = (course: ICourse, enrollment?: IStudentCourse | null): LastEnrollmentSelection => ({
        course: {
            ...course,
            progress: enrollment?.progress ?? null,
            enrollmentUpdatedAt: enrollment?.updated_at ?? null
        },
        status: enrollment?.status ?? null
    });

    const isEnrollmentFinished = (enrollment: IStudentCourse | null) => {
        if (!enrollment) return false;
        const numericProgress = enrollment.progress ?? 0;
        return enrollment.status === UserEnrollmentStatus.Completed || numericProgress >= 100;
    };

    const isCourseAvailable = (course: ICourse) => {
        const enrollment = enrollmentsByCourseId.get(course.id) ?? null;
        return !isEnrollmentFinished(enrollment);
    };

    const findFirstAvailableCourse = (predicate?: (course: ICourse) => boolean) => {
        for (const course of coursesByOrder) {
            if (predicate && !predicate(course)) continue;
            if (isCourseAvailable(course)) {
                return { course, enrollment: enrollmentsByCourseId.get(course.id) ?? null };
            }
        }
        return null;
    };

    const findNextInTrack = (trackCourses: ICourse[], currentOrder: number) => {
        return trackCourses.find((course) => course.orderNumber > currentOrder && isCourseAvailable(course));
    };

    const findPreviousInTrack = (trackCourses: ICourse[], currentOrder: number) => {
        for (let i = trackCourses.length - 1; i >= 0; i -= 1) {
            const course = trackCourses[i];
            if (course.orderNumber >= currentOrder) continue;
            if (isCourseAvailable(course)) {
                return course;
            }
        }
        return null;
    };

    const findOtherTrackCourse = (currentTrackTag: string | null) => {
        return findFirstAvailableCourse((course) => {
            const courseTrackTags = course.tags?.filter((tag) => tag.startsWith("track_")) ?? [];
            if (!courseTrackTags.length) return false;
            if (!currentTrackTag) return true;
            return !courseTrackTags.includes(currentTrackTag);
        });
    };

    const fallbackCandidate = findFirstAvailableCourse();

    const latestEnrollmentWithCourse = sortedEnrollments.find((enrollment) => courseMap.has(enrollment.course_id));
    if (!latestEnrollmentWithCourse) {
        return fallbackCandidate ? toSelection(fallbackCandidate.course, fallbackCandidate.enrollment) : null;
    }

    const currentCourse = courseMap.get(latestEnrollmentWithCourse.course_id);
    if (!currentCourse) {
        return fallbackCandidate ? toSelection(fallbackCandidate.course, fallbackCandidate.enrollment) : null;
    }

    const currentEnrollment = enrollmentsByCourseId.get(currentCourse.id) ?? null;
    const isFinished = isEnrollmentFinished(currentEnrollment);

    if (!isFinished) {
        return toSelection(currentCourse, currentEnrollment);
    }

    const trackTag = currentCourse.tags?.find((tag) => tag.startsWith("track_")) ?? null;

    if (trackTag) {
        const trackCourses = coursesByOrder.filter((course) => course.tags?.includes(trackTag));
        const nextCourse = findNextInTrack(trackCourses, currentCourse.orderNumber);
        if (nextCourse) {
            return toSelection(nextCourse, enrollmentsByCourseId.get(nextCourse.id) ?? null);
        }

        const previousCourse = findPreviousInTrack(trackCourses, currentCourse.orderNumber);
        if (previousCourse) {
            return toSelection(previousCourse, enrollmentsByCourseId.get(previousCourse.id) ?? null);
        }

        const otherTrackCandidate = findOtherTrackCourse(trackTag);
        if (otherTrackCandidate) {
            return toSelection(otherTrackCandidate.course, otherTrackCandidate.enrollment);
        }
    }

    return fallbackCandidate ? toSelection(fallbackCandidate.course, fallbackCandidate.enrollment) : null;
};

const getTrackLabelForCourse = (course: ICourse, courseTags: ICourseTag[]): string | null => {
    const normalizedTags = Array.from(
        new Set([...(course.tags ?? []), course.type === "challenge" ? "challenge" : null])
    ).filter(Boolean) as string[];

    if (!normalizedTags.length) return null;

    const matchingTag = normalizedTags
        .map((tag) => courseTags.find((courseTag) => courseTag.value === tag))
        .find((tag) => tag && (tag.value.startsWith("track_") || tag.value === "challenge"));

    if (matchingTag) return matchingTag.label;

    if (course.type === "challenge" || course.tags?.includes("challenge")) {
        return "Challenges";
    }

    return null;
};

const resolveHeroEnrollmentCourses = ({
    enrollmentCourses,
    courses,
    trackTags,
    primaryCourseId
}: LastEnrollmentSource): HeroCourseItem[] => {
    if (!courses.length) return [];

    const courseMap = new Map(courses.map((c) => [c.id, c]));
    const enrollmentsByCourseId = new Map(
        enrollmentCourses.filter((e) => courseMap.has(e.course_id)).map((e) => [e.course_id, e])
    );

    const isEnrollmentFinished = (enrollment: IStudentCourse | null) => {
        if (!enrollment) return false;
        return enrollment.status === UserEnrollmentStatus.Completed || (enrollment.progress ?? 0) >= 100;
    };

    const primaryCourse = courses.find((course) => course.id === primaryCourseId);
    const primaryEnrollment = primaryCourse ? (enrollmentsByCourseId.get(primaryCourse.id) ?? null) : null;
    // The onboarding course leads the hero, but only once it is actually started —
    // an untouched challenge has no place there.
    const shouldShowPrimaryCourse = primaryEnrollment !== null && !isEnrollmentFinished(primaryEnrollment);

    const startedEnrollments = enrollmentCourses
        .filter((e) => courseMap.has(e.course_id) && !isEnrollmentFinished(e))
        .sort((a, b) => (b.progress ?? 0) - (a.progress ?? 0));

    const result: HeroCourseItem[] = [];

    if (primaryCourse && shouldShowPrimaryCourse) {
        result.push({
            course: {
                ...primaryCourse,
                progress: primaryEnrollment?.progress ?? null,
                enrollmentUpdatedAt: primaryEnrollment?.updated_at ?? null
            },
            status: primaryEnrollment?.status ?? null,
            trackLabel: getTrackLabelForCourse(primaryCourse, trackTags)
        });
    }

    for (const enrollment of startedEnrollments) {
        const course = courseMap.get(enrollment.course_id);
        if (!course) continue;
        if (primaryCourse && course.id === primaryCourse.id) continue;

        result.push({
            course: {
                ...course,
                progress: enrollment.progress ?? null,
                enrollmentUpdatedAt: enrollment.updated_at ?? null
            },
            status: enrollment.status ?? null,
            trackLabel: getTrackLabelForCourse(course, trackTags)
        });
    }

    return result;
};
